import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Clock, Code2, Users, Building2, CreditCard, Zap, Check } from "lucide-react";
import { MorrMark } from "@/components/MorrMark";

const SNIPPET = `<script
  src="https://cdn.morrcart.com/embed.js"
  data-tenant="abccoffee"
  data-api="https://api.morrcart.com">
</script>

<!-- Your buttons, your words, your style -->
<button onclick="MorrCart.add(product)">Order Now</button>`;

function Nav() {
  return (
    <nav className="sticky top-0 z-40 border-b border-border bg-background/80 backdrop-blur-md">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
          <MorrMark size={28} />
          <span className="font-display font-extrabold tracking-tighter text-xl">MorrCart</span>
        </Link>
        <div className="flex items-center gap-2 sm:gap-3">
          <Link to="/demo"><Button variant="ghost" size="sm" data-testid="nav-demo-btn" className="rounded-none">Live Demo</Button></Link>
          <Link to="/portal"><Button variant="ghost" size="sm" data-testid="nav-portal-btn" className="rounded-none hidden sm:inline-flex">Customer Portal</Button></Link>
          <Link to="/login"><Button size="sm" data-testid="nav-login-btn" className="rounded-none">Operator Login</Button></Link>
        </div>
      </div>
    </nav>
  );
}

const features = [
  { icon: Code2, title: "One snippet. Any site.", body: "Drop a single script tag into any website. No redesign, no framework lock-in. Your buttons and wording stay exactly as they are." },
  { icon: Clock, title: "Average-ready timing", body: "Start a ticket timer on prep, stop it on ready. MorrCart learns your rhythm and shows customers a live \"Ready at 4:32pm\"." },
  { icon: Building2, title: "Grey-label subdomains", body: "Every business gets abcshop.morrcart.com. Fully tenant-controlled — they rename their subdomain anytime." },
  { icon: CreditCard, title: "Cash now, cards next", body: "Take offline cash orders in person today. Stripe Connect payouts are wired for the next phase." },
  { icon: Users, title: "CRM + Loyalty built-in", body: "Every order builds a contact record and loyalty stamps. Visible per role: platform, tenant, or customer." },
  { icon: Zap, title: "Pickup now or later", body: "Customers schedule pickup instantly or for later. Orders flow onto a live kanban board for the shop." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Nav />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 grid-lines opacity-[0.4] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 py-24 lg:py-32 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 animate-slide-up">
              <div className="inline-flex items-center gap-2 border border-border bg-secondary px-3 py-1 mb-6">
                <span className="h-1.5 w-1.5 bg-accent" />
                <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Multitenant Cart Platform</span>
              </div>
              <h1 className="font-display font-extrabold tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
                A secure shopping cart<br />for every local business
                <span className="text-primary">.</span>
              </h1>
              <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">
                Onboard any coffee shop, barbershop, gym or trainer. Paste one snippet into
                their existing site and their cart, checkout, scheduling and notifications go live —
                without touching their design.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Link to="/demo"><Button size="lg" className="rounded-none gap-2 h-12 px-6" data-testid="hero-demo-btn">See it live <ArrowRight className="h-4 w-4" /></Button></Link>
                <Link to="/login"><Button size="lg" variant="outline" className="rounded-none h-12 px-6" data-testid="hero-login-btn">Operator login</Button></Link>
              </div>
              <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground">
                {["No design changes", "Offline cash orders", "Live ready-timers"].map((t) => (
                  <span key={t} className="inline-flex items-center gap-2"><Check className="h-4 w-4 text-[hsl(var(--success))]" />{t}</span>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="bg-[#0A0A0A] text-[#E4E4E7] border border-border shadow-2xl">
                <div className="flex items-center gap-1.5 px-4 py-3 border-b border-white/10">
                  <span className="h-2.5 w-2.5 rounded-full bg-[#FF5F56]" />
                  <span className="h-2.5 w-2.5 rounded-full bg-[#FFBD2E]" />
                  <span className="h-2.5 w-2.5 rounded-full bg-[#27C93F]" />
                  <span className="ml-3 text-xs font-mono text-white/40">index.html</span>
                </div>
                <pre className="p-5 text-[13px] leading-relaxed font-mono overflow-x-auto no-scrollbar whitespace-pre"><code>{SNIPPET}</code></pre>
              </div>
              <p className="mt-3 text-xs text-muted-foreground font-mono">// That's the whole integration.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <h2 className="font-display font-extrabold tracking-tighter text-2xl sm:text-3xl lg:text-4xl max-w-2xl">
          Everything a local shop needs, nothing they have to learn.
        </h2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-border">
          {features.map((f) => (
            <div key={f.title} className="border-r border-b border-border p-8 group hover:bg-secondary transition-colors" data-testid={`feature-${f.title.split(" ")[0].toLowerCase()}`}>
              <f.icon className="h-6 w-6 text-primary" />
              <h3 className="mt-5 font-display font-bold text-lg tracking-tight">{f.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Roles */}
      <section className="border-y border-border bg-secondary">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">One platform, three views</span>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-px bg-border border border-border">
            {[
              { t: "Platform Owner", d: "See every tenant, all orders & CRM, platform-wide loyalty. Switch into any tenant for support.", tag: "SuperAdmin" },
              { t: "Business Owner", d: "Manage menu, run the live order board with ready-timers, own their customers & subdomain.", tag: "Tenant" },
              { t: "Customer", d: "Track orders & pickups, view payments, check loyalty stamps, edit their account.", tag: "Customer" },
            ].map((r) => (
              <div key={r.t} className="bg-background p-8">
                <span className="font-mono text-xs text-accent">{r.tag}</span>
                <h3 className="mt-2 font-display font-bold text-xl tracking-tight">{r.t}</h3>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{r.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 py-24 text-center">
        <h2 className="font-display font-extrabold tracking-tighter text-3xl sm:text-4xl lg:text-5xl">Ready to see a real store?</h2>
        <p className="mt-4 text-muted-foreground">Explore the Abc Coffee Shop demo — a live host site running the MorrCart widget.</p>
        <div className="mt-8 flex justify-center gap-3">
          <Link to="/demo"><Button size="lg" className="rounded-none gap-2 h-12 px-8" data-testid="cta-demo-btn">Open the demo <ArrowRight className="h-4 w-4" /></Button></Link>
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <span className="font-display font-extrabold tracking-tighter text-foreground">MorrCart</span>
          <div className="flex items-center gap-4">
            <span>Powered by MorrAccess entitlements · morrcart.com</span>
            <Link to="/login" className="text-muted-foreground/70 hover:text-foreground underline underline-offset-4 transition-colors" data-testid="footer-login-link">Sign in</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
